import { useReactiveVar } from '@apollo/client'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'
import {
    defaultGlobalStyles,
    defaultUnitOfMeasureStyles,
    projectStyle,
} from 'api/graphQL/reactiveVars/styleGuides'
import clsx from 'clsx'
import { ProjectSettings } from 'models/projectSettings'
import {
    ImperialStyleInput,
    UnitOfMeasureStylesInput,
} from 'models/projectStyle'
import { getDisplayText } from 'pages/ProjectsPage/WorkspacePage/components/Common/ContentItemElementText'
import { getIsElementHaveUnits } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import React from 'react'
import { v4 as newGuid } from 'uuid'
import { GlobalStyleInput } from './types'

type StyleProps = {
    uomStyleInput: UnitOfMeasureStylesInput
    globalStyleInput: GlobalStyleInput
}

const getStyles = (
    globalStyleInput: GlobalStyleInput,
    styleIput: ImperialStyleInput
) => {
    const {
        overwriteMasterFormatStyles,
        overwriteFontStyles,
        font: globalFont,
    } = globalStyleInput
    return {
        color: overwriteFontStyles ? globalFont.fontColor : styleIput.fontColor,
        fontFamily: overwriteMasterFormatStyles
            ? globalFont?.fontFamily
            : styleIput.fontFamily,
        fontSize: `${
            overwriteMasterFormatStyles
                ? globalFont?.fontSize
                : styleIput.fontSize
        }pt`,
        fontWeight: overwriteFontStyles
            ? globalFont?.fontStyles?.includes('BOLD') && 'bold'
            : styleIput?.fontStyles?.includes('BOLD') && 'bold',
        fontStyle: overwriteFontStyles
            ? globalFont?.fontStyles?.includes('ITALIC') && 'italic'
            : styleIput?.fontStyles?.includes('ITALIC') && 'italic',
        textDecoration: overwriteFontStyles
            ? globalFont?.fontStyles?.includes('UNDERLINE') && 'underline'
            : styleIput?.fontStyles?.includes('UNDERLINE') && 'underline',
    } as object
}

const useStyles = makeStyles((theme: Theme) => ({
    data: {
        display: 'inline',
        wordBreak: 'keep-all',
        textAlign: 'justify',
    },
    missingdata: {
        display: 'inline',
        fontWeight: 'bold',
    },
    IP: ({ uomStyleInput, globalStyleInput }: StyleProps) => ({
        ...getStyles(globalStyleInput, uomStyleInput.imperial),
    }),
    SI: ({ uomStyleInput, globalStyleInput }: StyleProps) => ({
        ...getStyles(globalStyleInput, uomStyleInput.metric),
    }),
    row: {
        display: 'table-row',
        wordBreak: 'break-word',
    },

    cell: {
        display: 'table-cell',
    },

    PRspace: { marginLeft: theme.spacing(3.5) },
    PR1: {
        marginLeft: theme.spacing(3),
    },
    PR2: {
        marginLeft: theme.spacing(5),
        display: 'flex',
    },
    PR3: {
        marginLeft: theme.spacing(10),
    },
    PR4: {
        marginLeft: theme.spacing(13.5),
    },
    PR5: {
        marginLeft: theme.spacing(17),
    },
    flexAdjust: {
        verticalAlign: 'top',
    },
}))

export const formatSection = (
    formatString: string,
    familyNumberFormat: string = 'XXXXXX'
) => {
    if (familyNumberFormat === 'XXXXXX') return formatString
    let result
    const regex = /Section/gi
    while ((result = regex.exec(formatString))) {
        const value = (formatString
            .substring(result.index + 8, result.index + 14)
            .match(/\d{6}/) || '')[0]
        if (value && value !== '') {
            if (familyNumberFormat === 'XX_XX_XX')
                formatString = formatString.replace(
                    value,
                    value.match(/.{1,2}/g)!.join(' ')
                )
            else if (familyNumberFormat === 'XX_XXXX')
                formatString = formatString.replace(
                    value,
                    value.substring(0, 2)! + ' ' + value.substring(2, 6)!
                )
        }
    }
    return formatString
}

export const convertNumtoAlpha = (num: number) => {
    let alphaChar = ''
    let absoluteNum
    let valNum = num.valueOf()

    const uppercaseAlpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

    if (valNum <= 26) {
        while (valNum > 0) {
            absoluteNum = (valNum - 1) % 26
            alphaChar = String.fromCharCode(65 + absoluteNum) + alphaChar
            valNum = ((valNum - absoluteNum) / 26) | 0
        }
    } else {
        absoluteNum = (valNum - 1) % 26
        alphaChar = uppercaseAlpha[absoluteNum] + uppercaseAlpha[absoluteNum]
    }

    return alphaChar || undefined
}
export const hLevelChar = (
    hierarchicalLevel: number = 0,
    parentCount: number
) => {
    switch (hierarchicalLevel) {
        case 2:
            return `${convertNumtoAlpha(parentCount)}.`
        case 3:
            return `${parentCount}.`
        case 4:
            return `${convertNumtoAlpha(parentCount)?.toLowerCase()}.`
        case 5:
            return `${parentCount})`
        case 6:
            return `${convertNumtoAlpha(parentCount)?.toLowerCase()})`
        case 7:
            return `(${parentCount})`
        case 8:
            return `(${convertNumtoAlpha(parentCount)?.toLowerCase()})`
        default:
            return ''
    }
}
export const indent = (
    hierarchicalLevel: number = 0,
    classes: ClassNameMap
) => {
    switch (hierarchicalLevel) {
        case -1:
            return
        case 2:
            return <span className={classes.PR1}></span>
        case 3:
            return <span className={classes.PR2}></span>
        case 4:
            return <span className={classes.PR3}></span>
        case 5:
            return <span className={classes.PR4}></span>
        case 6:
            return <span className={classes.PR5}></span>
        case 7:
            return <span className={classes.PR5}></span>
        case 8:
            return <span className={classes.PR5}></span>
        default:
            return <span className={classes.PR5}></span>
    }
}
type UOMStyleInput = UnitOfMeasureStylesInput
export interface PreviewContentProps {
    data?: Partial<TextContentElement>[]
    hierarchicalLevel?: number
    settings?: Partial<ProjectSettings>
    isDataFromArticle?: boolean
    uomStyleInput?: UnitOfMeasureStylesInput
    globalStyleInput?: GlobalStyleInput
    hLevelChar?: string | number
    isInArticle?: boolean
}

const IsFireFox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1

const PreviewContent: React.FC<PreviewContentProps> = ({
    data,
    hierarchicalLevel,
    settings,
    isDataFromArticle,
    uomStyleInput = defaultUnitOfMeasureStyles,
    globalStyleInput = {
        ...defaultGlobalStyles,
        overwriteFontStyles: false,
        overwriteMasterFormatStyles: false,
    },
    hLevelChar,
    isInArticle = true,
}) => {
    const classes = useStyles({ uomStyleInput, globalStyleInput })

    const renderUnitsOfMeasure = (
        element: Partial<TextContentElement>,
        style: string
    ) => {
        const unitOfMeasure = settings?.unitOfMeasure
            ? settings?.unitOfMeasure.toString()
            : 'BOTH'

        switch (unitOfMeasure) {
            case 'BOTH': {
                const hasImperialUnits = !!element?.imperialUnits?.trim()
                const hasMetricUnits = !!element?.metricUnits?.trim()

                if (hasImperialUnits && hasMetricUnits) {
                    return (
                        <div className={style}>
                            <span className={classes.IP}>
                                {element?.imperialUnits}
                            </span>
                            &nbsp;
                            <span className={classes.SI}>
                                ({element?.metricUnits})
                            </span>
                        </div>
                    )
                } else if (hasImperialUnits) {
                    return (
                        <div className={style}>
                            <span className={classes.IP}>
                                {element?.imperialUnits}
                            </span>
                        </div>
                    )
                } else if (hasMetricUnits) {
                    return (
                        <div className={style}>
                            <span className={classes.SI}>
                                {element?.metricUnits}
                            </span>
                        </div>
                    )
                }
                break
            }
            case 'METRIC':
            case 'IMPERIAL':
                return (
                    <div className={style}>
                        <span
                            className={
                                unitOfMeasure === 'METRIC'
                                    ? classes.SI
                                    : classes.IP
                            }
                        >
                            {unitOfMeasure === 'METRIC'
                                ? element?.metricUnits
                                : element?.imperialUnits}
                        </span>
                    </div>
                )
        }
    }

    const {
        sectionStyles: { sectionTitleStyle },
    } = useReactiveVar(projectStyle)

    let content = [<div></div>]

    const setSelectionOption = (
        eachElement: Partial<TextContentElement>,
        style: string,
        optedIn: boolean,
        settings?: Partial<ProjectSettings>,
        currentIndex?: number
    ) => {
        const unitOfMeasure = settings?.unitOfMeasure
            ? settings?.unitOfMeasure.toString()
            : 'BOTH'
        const familyNumberFormat = settings?.familyNumberFormat
            ? settings?.familyNumberFormat
            : 'XXXXXX'
        const isBothUnitsActive = unitOfMeasure === 'BOTH'
        const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
        const isMetricUnitsActive = unitOfMeasure === 'METRIC'
        const isElementHaveUnits = getIsElementHaveUnits(eachElement)
        const isShowImperial =
            isElementHaveUnits && (isBothUnitsActive || isImperialUnitsActive)
        const isShowMetric =
            isElementHaveUnits && (isBothUnitsActive || isMetricUnitsActive)
        const displayText = getDisplayText(
            eachElement,
            isShowMetric,
            isShowImperial,
            settings?.unitOfMeasure
        )
        const previousItemState = currentIndex
            ? data?.[currentIndex - 1]?.optionItemState
            : ''
        const isReplacementOption = eachElement.type === 'ReplacementOption'
        const isSelectionOption = eachElement.type === 'SelectionOption'
        const hasTextValue = eachElement.text?.trim() !== ''
        const isActive = eachElement.optionItemState !== 'InActive'
        const hasChildElements = !!eachElement.elements?.length
        const hasUOMElementAsChild = eachElement.elements?.some(
            (element) =>
                element?.type ===
                PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT.UnitsOfMeasure
        )
        if (
            isSelectionOption &&
            hasTextValue &&
            isActive &&
            !hasUOMElementAsChild
        ) {
            content.push(
                optedIn ? (
                    <div className={clsx(style)}>
                        {previousItemState === 'InActive' ? '' : ' '}
                        {formatSection(displayText!, familyNumberFormat)}
                    </div>
                ) : !isDataFromArticle ? (
                    <div className={clsx(style)}>
                        {previousItemState === 'InActive' ? '' : ' '}[
                        {formatSection(displayText!, familyNumberFormat)}]
                    </div>
                ) : (
                    <div className={clsx(style)}>
                        {previousItemState === 'InActive' ? '' : ' '}
                        {formatSection(displayText!, familyNumberFormat)}
                    </div>
                )
            )
        }
        if (
            isReplacementOption &&
            hasTextValue &&
            isActive &&
            !hasUOMElementAsChild
        ) {
            content.push(
                optedIn ? (
                    <div className={clsx(style)}>
                        {' '}
                        {previousItemState === 'InActive' ? '' : ' '}
                        {displayText}
                    </div>
                ) : (
                    <div className={clsx(style)}>
                        {previousItemState === 'InActive' ? '' : ' '}&lt;
                        {displayText}&gt;
                    </div>
                )
            )
        }
        if (
            (isSelectionOption || isReplacementOption) &&
            hasChildElements &&
            isActive &&
            hasUOMElementAsChild
        ) {
            if (!optedIn) {
                if (eachElement.type === 'SelectionOption') {
                    content.push(<div className={clsx(style)}>[</div>)
                } else {
                    content.push(
                        <div className={clsx(style)}>
                            {' '}
                            {previousItemState === 'InActive' ? '' : ' '}&lt;
                        </div>
                    )
                }
            }

            eachElement.elements?.map(
                (eachChildElement: Partial<TextContentElement>) => {
                    const isBothUnitsActive = unitOfMeasure === 'BOTH'
                    const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
                    const isMetricUnitsActive = unitOfMeasure === 'METRIC'
                    const isElementHaveUnits = getIsElementHaveUnits(
                        eachElement
                    )
                    const isShowImperial =
                        isElementHaveUnits &&
                        (isBothUnitsActive || isImperialUnitsActive)
                    const isShowMetric =
                        isElementHaveUnits &&
                        (isBothUnitsActive || isMetricUnitsActive)
                    const childDisplayText = getDisplayText(
                        eachChildElement,
                        isShowMetric,
                        isShowImperial,
                        settings?.unitOfMeasure
                    )
                    switch (eachChildElement.type) {
                        case 'Text':
                            {
                                content.push(
                                    <div className={clsx(style)}>
                                        &nbsp;{childDisplayText}
                                    </div>
                                )
                            }
                            break
                        case 'UnitsOfMeasure':
                            switch (unitOfMeasure) {
                                case 'BOTH':
                                    const hasImperialUnits = !!eachChildElement?.imperialUnits?.trim()
                                    const hasMetricUnits = !!eachChildElement?.metricUnits?.trim()

                                    if (hasImperialUnits && hasMetricUnits) {
                                        content.push(
                                            <div className={style}>
                                                <span className={classes.IP}>
                                                    {eachChildElement?.imperialUnits}
                                                </span>
                                                &nbsp;
                                                <span className={classes.SI}>
                                                    ({eachChildElement?.metricUnits})
                                                </span>
                                            </div>
                                        )
                                    } else if (hasImperialUnits) {
                                        content.push(
                                            <div className={style}>
                                                <span className={classes.IP}>
                                                    {eachChildElement?.imperialUnits}
                                                </span>
                                            </div>
                                        )
                                    } else if (hasMetricUnits) {
                                        content.push(
                                            <div className={style}>
                                                <span className={classes.SI}>
                                                    {eachChildElement?.metricUnits}
                                                </span>
                                            </div>
                                        )
                                    }
                                    break
                                case 'METRIC':
                                case 'IMPERIAL':
                                    content.push(
                                        <div className={style}>
                                            <span
                                                className={
                                                    unitOfMeasure === 'METRIC'
                                                        ? classes.SI
                                                        : classes.IP
                                                }
                                            >
                                                {childDisplayText}
                                            </span>
                                        </div>
                                    )
                                    break
                            }
                            break
                    }
                }
            )
            if (!optedIn) {
                if (eachElement.type === 'SelectionOption') {
                    content.push(<div className={clsx(style)}>]</div>)
                } else {
                    content.push(
                        <div className={clsx(style)}>
                            {previousItemState === 'InActive' ? '' : ' '}&gt;
                        </div>
                    )
                }
            }
        }
    }

    data?.map((each: Partial<TextContentElement>, index: number) => {
        switch (each.type) {
            case 'Text':
            case 'SectionNumber':
            case 'SectionName':
                content.push(
                    // Check if new line
                    each.text === '\v' ? (
                        <div
                            data-testid="PrintPreviewNewLine"
                            style={{
                                marginTop:
                                    sectionTitleStyle.spaceBetweenLines ?? 0,
                            }}
                        ></div>
                    ) : (
                        <div className={clsx(classes.data)}>{each.text}</div>
                    )
                )
                break
            case 'OptionGroup':
                if (each.elements && each.elements?.length > 0) {
                    if (
                        !!each.elements.find(
                            (e) => e.optionItemState === 'OptedIn'
                        )
                    ) {
                        each.elements.map(
                            (eachElement: Partial<TextContentElement>) => {
                                if (eachElement.type === 'Text') {
                                    content.push(
                                        <div className={clsx(classes.data)}>
                                            {' '}
                                            {eachElement.text}
                                        </div>
                                    )
                                }
                                if (eachElement.optionItemState === 'OptedIn') {
                                    setSelectionOption(
                                        eachElement,
                                        classes.data,
                                        true,
                                        settings,
                                        index
                                    )
                                }
                                if (eachElement.type === 'UnitsOfMeasure') {
                                    const uomElement = renderUnitsOfMeasure(eachElement, classes.data)
                                    if (uomElement) {
                                        content.push(uomElement)
                                    }
                                }
                            }
                        )
                    } else {
                        each.elements.map(
                            (eachElement: Partial<TextContentElement>) => {
                                if (
                                    (eachElement.changeType?.toUpperCase() !==
                                        'DELETED' &&
                                        eachElement.elements?.length === 0) ||
                                    (eachElement.changeType?.toUpperCase() !==
                                        'DELETED' &&
                                        eachElement.elements &&
                                        eachElement.elements[0]?.changeType?.toUpperCase() !==
                                            'DELETED')
                                ) {
                                    if (eachElement.type === 'Text') {
                                        content.push(
                                            <div className={clsx(classes.data)}>
                                                {' '}
                                                {eachElement.text}
                                            </div>
                                        )
                                    }
                                    if (eachElement.type === 'UnitsOfMeasure') {
                                        const uomElement = renderUnitsOfMeasure(
                                            eachElement,
                                            !isDataFromArticle
                                                ? classes.missingdata
                                                : classes.data
                                        )
                                        if (uomElement) {
                                            content.push(uomElement)
                                        }
                                    } else {
                                        setSelectionOption(
                                            eachElement,
                                            !isDataFromArticle
                                                ? classes.missingdata
                                                : classes.data,
                                            false,
                                            settings,
                                            index
                                        )
                                    }
                                }
                            }
                        )
                    }
                }
                break
            case 'SelectionOption':
                break
            case 'UnitsOfMeasure':
                let unitOfMeasure = settings?.unitOfMeasure
                    ? settings?.unitOfMeasure
                    : 'BOTH'
                const isBothUnitsActive = unitOfMeasure === 'BOTH'
                const isImperialUnitsActive = unitOfMeasure === 'IMPERIAL'
                const isMetricUnitsActive = unitOfMeasure === 'METRIC'
                const isElementHaveUnits =
                    each.type === 'UnitsOfMeasure' ||
                    ((each.type === 'SelectionOption' ||
                        each.type === 'ReplacementOption') &&
                        each.elements?.length! > 0 &&
                        each.elements?.some(
                            (element) =>
                                element.imperialUnits !== '' ||
                                element.metricUnits !== ''
                        ))
                const isShowImperial =
                    isElementHaveUnits &&
                    (isBothUnitsActive || isImperialUnitsActive)
                const isShowMetric =
                    isElementHaveUnits &&
                    (isBothUnitsActive || isMetricUnitsActive)
                const displayText = getDisplayText(
                    each,
                    isShowMetric,
                    isShowImperial,
                    settings?.unitOfMeasure
                )
                switch (unitOfMeasure) {
                    case 'BOTH':
                        const standaloneHasImperialUnits = !!each.imperialUnits?.trim()
                        const standaloneHasMetricUnits = !!each.metricUnits?.trim()

                        if (standaloneHasImperialUnits && standaloneHasMetricUnits) {
                            content.push(
                                <div className={classes.data}>
                                    <span className={classes.IP}>
                                        {each.imperialUnits}
                                    </span>
                                    &nbsp;
                                    <span className={classes.SI}>
                                        ({each.metricUnits})
                                    </span>
                                </div>
                            )
                        } else if (standaloneHasImperialUnits) {
                            content.push(
                                <div className={classes.data}>
                                    <span className={classes.IP}>
                                        {each.imperialUnits}
                                    </span>
                                </div>
                            )
                        } else if (standaloneHasMetricUnits) {
                            content.push(
                                <div className={classes.data}>
                                    <span className={classes.SI}>
                                        {each.metricUnits}
                                    </span>
                                </div>
                            )
                        }
                        break
                    case 'METRIC':
                    case 'IMPERIAL':
                        content.push(
                            <div className={classes.data}>
                                <span
                                    className={
                                        unitOfMeasure === 'METRIC'
                                            ? classes.SI
                                            : classes.IP
                                    }
                                >
                                    {displayText}
                                </span>
                            </div>
                        )
                        break
                }
                break
            case 'Hyperlink':
                content.push(
                    <div className={clsx(classes.missingdata)}>
                        [{each.text}]
                    </div>
                )
                break
        }
    })
    return (
        <div className={classes.row}>
            {(hierarchicalLevel && hierarchicalLevel != 1) || !isInArticle ? (
                <div className={clsx(classes.cell)}>
                    {hLevelChar}
                    {(hierarchicalLevel && hierarchicalLevel >= 2) ||
                    !isInArticle ? (
                        <span className={classes.PRspace}></span>
                    ) : (
                        ''
                    )}
                </div>
            ) : (
                ''
            )}

            <div className={clsx(classes.cell)}>
                {content.map((element) => (
                    <div
                        data-testid="PrintPreviewElements"
                        className={clsx(classes.data)}
                        key={newGuid()}
                    >
                        {element}
                    </div>
                ))}
            </div>
        </div>
    )
}

export default PreviewContent
